import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import SubNav from "../../shared/subnav/SubNav";
import { CircularProgress, Grid, Typography, Box } from "@mui/material";
import fordStyles from "../dashboard/dealerDashBoard/DealerReporting/DealerReporting.module.scss";
import lincolnStyles from "../dashboard/dealerDashBoard/DealerReporting/DealerReportingLincoln.module.scss";
import {
  IS_CANADA_MARKET,
  IS_FORD_BRAND,
  NOTNULL_OR_NOTUNDEFINED,
} from "../../utils/EmpUtil";
import BrandSettingsClient from "../../clients/BrandSettingsClient";
import { logMsg } from "../../common/Logger";
import DealerReportingClient from "../../clients/DealerReportingClient";
import { base64FileDownload } from "../../utils/FileDownloadUtil";
import HasPermissionTo from "../../common/HasPermissionTo";
import UpperCaseText from "../../utils/UpperCaseText";
import UiDateUtils from "../../utils/UiDateUtils";
import ReportFilter from "./ReportFilter";
import * as AnalyticsService from "../../shared/services/analytics/AnalyticsService";
import EMPButton from "../../shared/components/EMPButton/EMPButton";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import VisibilityIcon from "@mui/icons-material/Visibility";

const NADealerReporting = ({ history, t, user, userDetails, ...props }) => {
  const [dateRangeMethod, setDateRangeMethod] = useState("auto");
  const [dateRange, setDateRange] = useState(
    t("fieldReporting.dropDowns.last30Days")
  );
  const [dateRanges] = useState([
    t("fieldReporting.dropDowns.last30Days"),
    t("fieldReporting.dropDowns.last90Days"),
    t("fieldReporting.dropDowns.yearToDate"),
  ]);
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [downloadingReports, setDownloadingReports] = useState(new Set());
  const [reports, setReports] = useState([
    {
      key: "ddtUtilizationReportKey",
      name: t("dealerReporting.reportDdtUtilizationName"),
      type: "ddtUtilization",
      brandOnly: "ford",
    },
    {
      key: "lincolnDdtUtilizationReportKey",
      name: t("dealerReporting.reportDdtUtilizationName"),
      type: "ddtUtilizationLincoln",
      brandOnly: "lincoln",
    },
    {
      key: "openTransactionsReportKey",
      name: t("dealerReporting.reportOpenTransactionsName"),
      type: "openTransactions",
    },
    {
      key: "dealerReservationsReportKey",
      name: t("dealerReporting.reportReservationsName"),
      type: "dealerReservation",
    },
    {
      key: "orderConversionReportKey",
      name: t("dealerReporting.reportOrderConversionName"),
      type: "orderConversion",
    },
    {
      key: "purchaseRequestsReportKey",
      name: t("dealerReporting.reportPurchaseRequestName"),
      type: "purchaseRequest",
    },
    {
      key: "modelEReportKey",
      name: t("dealerReporting.reportModelEName"),
      type: "modelE",
    },
  ]);

  const isFordBrand = useMemo(() => IS_FORD_BRAND(user.brand), [user.brand]);
  const isCanadaMarket = useMemo(
    () => IS_CANADA_MARKET(user.market),
    [user.market]
  );
  const reportPermissions = useMemo(
    () => user.permissions.rolePermissions,
    [user.permissions.rolePermissions]
  );

  const getTodayDate = () => {
    const today = new Date();
    return `${today.getMonth() + 1}-${today.getDate()}-${today.getFullYear()}`;
  };

  const redirectToAccessoryPricingSearch = useCallback(() => {
    history.push({
      pathname: `/pricing/accessories`,
      state: { redirectToAccSearch: true },
    });
  }, [history]);

  const trackUserAction = useCallback(
    (reportName) => {
      AnalyticsService.trackOnDownloadEvent(
        "fv:emp:reporting",
        `emp:reporting:download:report name:${reportName}`,
        "fv:emp:reporting:download",
        user
      );
    },
    [user]
  );

  const handleFromDateChange = useCallback((date) => {
    if (date !== null) {
      setFromDate(date);
    }
  }, []);

  const handleToDateChange = useCallback((date) => {
    if (date !== null) {
      setToDate(date);
    }
  }, []);

  const handleDateRangeChange = useCallback((e) => {
    setDateRange(e.target.value);
  }, []);

  const toggleRadio = useCallback((e) => {
    setDateRangeMethod(e.target.value);
  }, []);

  const downloadReport = useCallback(
    (reportType, report) => {
      console.log(
        `[NADealerReporting.downloadReport()] Downloading ${reportType} Report`
      );
      setDownloadingReports((prevState) => new Set([...prevState, report.key]));
      const isManualDateRange = dateRangeMethod === "manual";

      const reqData = {
        dateRange: isManualDateRange ? "" : dateRange,
        dateFrom: isManualDateRange
          ? UiDateUtils.formatDateMmDdYyyy(fromDate)
          : "",
        dateTo: isManualDateRange ? UiDateUtils.formatDateMmDdYyyy(toDate) : "",
      };

      console.log(
        "[NADealerReporting.downloadReport()] Date Request Data for Report:",
        reqData
      );

      const isFieldView = UpperCaseText(user.userType) === "FIELD_VIEW";
      let downloadRequest;

      switch (reportType) {
        case "purchaseRequest":
          downloadRequest =
            DealerReportingClient.downloadPurchaseRequestReportInCsv(
              user,
              user.lang,
              "",
              true,
              true,
              reqData,
              isFieldView
            );
          break;

        case "modelE":
          downloadRequest = DealerReportingClient.downloadModelEReportInCsv(
            user,
            user.lang,
            "",
            true,
            true,
            reqData,
            isFieldView
          );
          break;

        case "ddtUtilization":
        case "ddtUtilizationLincoln":
          downloadRequest =
            DealerReportingClient.downloadDdtUtilizationReportInCsv(
              user,
              reqData
            );
          break;

        case "dealerReservation":
          downloadRequest =
            DealerReportingClient.downloadDealerReservationReportInCsv(
              user,
              user.lang,
              "",
              true,
              reqData,
              isFieldView
            );
          break;

        case "orderConversion":
          downloadRequest =
            DealerReportingClient.downloadOrderConversionReportInCsv(
              user,
              user.lang,
              "",
              true,
              reqData,
              isFieldView
            );
          break;

        case "openTransactions":
          downloadRequest =
            DealerReportingClient.downloadOpenTransactionsReportInCsv(
              user,
              user.lang,
              "",
              reqData,
              true,
              isFieldView
            );
          break;

        default:
          console.error("Unknown report type:", reportType);
          setDownloadingReports((prevState) => {
            const nextState = new Set(prevState);
            nextState.delete(report.key);
            return nextState;
          });
          return;
      }

      console.log(
        "[NADealerReporting.downloadReport()] Download request ",
        downloadRequest
      );

      downloadRequest
        .then((res) => {
          if (reportType === "openTransactions" && !res) {
            return;
          }

          const filename = t(report.name) + " - " + getTodayDate() + ".xlsx";
          const BOM = "\uFEFF";
          const response = BOM + res;

          base64FileDownload(
            decodeURIComponent(response),
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            filename
          );
        })
        .catch((reason) => {
          logMsg("reason", reason);
        })
        .finally(() => {
          setDownloadingReports((prevState) => {
            const nextState = new Set(prevState);
            nextState.delete(report.key);
            return nextState;
          });
        });
    },
    [dateRange, dateRangeMethod, fromDate, t, toDate, user]
  );

  const downloadDdtUtilizationReport = useCallback(
    (report) => {
      console.log(
        "[NADealerReporting.downloadDdtUtilizationReport()] Downloading DDT Utilization Report"
      );
      downloadReport(report.type, report);
    },
    [downloadReport]
  );

  const handleReportAction = useCallback(
    (report) => {
      switch (report.type) {
        case "purchaseRequest":
        case "modelE":
        case "dealerReservation":
        case "orderConversion":
        case "openTransactions":
          downloadReport(report.type, report);
          return;
        case "ddtUtilization":
        case "ddtUtilizationLincoln":
          downloadDdtUtilizationReport(report);
          return;
        case "accessories":
          redirectToAccessoryPricingSearch();
          return;
        default:
          console.error("Unknown report type:", report.type);
      }
    },
    [
      downloadDdtUtilizationReport,
      downloadReport,
      redirectToAccessoryPricingSearch,
    ]
  );

  const dealerBrandSettingsCheck = useCallback(() => {
    if (!isFordBrand || isCanadaMarket) {
      return;
    }

    BrandSettingsClient.getDealerBrandAdjustments(user)
      .then(({ laborRate, markup }) => {
        if (
          NOTNULL_OR_NOTUNDEFINED(laborRate) &&
          NOTNULL_OR_NOTUNDEFINED(markup)
        ) {
          setReports((prevState) => [
            ...prevState,
            {
              key: "accessoriesReportKey",
              name: t("dealerReporting.reportAccessoriesName"),
              type: "accessories",
            },
          ]);
        }
      })
      .catch((error) => logMsg("reason", error));
  }, [isCanadaMarket, isFordBrand, t, user]);

  useEffect(() => {
    console.log("[NADealerReporting] landed on Dealer reporting page");
    AnalyticsService.trackPageLoadEvent("fv:emp:reporting", user);
    dealerBrandSettingsCheck();
  }, [dealerBrandSettingsCheck, user]);

  const styles = isFordBrand ? fordStyles : lincolnStyles;

  const getReport = (report) => {
    const isDownloading = downloadingReports.has(report.key);
    return (
      <Grid
        container
        className={styles.gridBody}
        key={report.key}
        sx={{ height: "65px", alignItems: "center" }}
      >
        <Grid item xs={6} sm={9}>
          <Typography variant="body1">{report.name}</Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          {isDownloading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="subtitle2">Downloading</Typography>
              <CircularProgress size={16} />
            </Box>
          ) : (
            <EMPButton
              onClick={() => {
                trackUserAction(report.name);
                handleReportAction(report);
              }}
              variant="text"
              leftIcon={<CloudDownloadIcon />}
            >
              {t("dealerReporting.download")}
            </EMPButton>
          )}
        </Grid>
      </Grid>
    );
  };

  const getReportWithPermission = (report) => {
    const renderAccessoriesReport = () => {
      const isDownloading = downloadingReports.has(report.key);
      return (
        <Grid
          container
          className={styles.gridBody}
          key={report.key}
          sx={{ minHeight: "60px", alignItems: "center" }}
        >
          <Grid item xs={6} sm={9}>
            <Typography variant="body1">{report.name}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            {isDownloading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2">Loading</Typography>
                <CircularProgress size={16} />
              </Box>
            ) : (
              <EMPButton
                onClick={() => handleReportAction(report)}
                variant="text"
                leftIcon={<VisibilityIcon />}
              >
                {t("dealerReporting.view")}
              </EMPButton>
            )}
          </Grid>
        </Grid>
      );
    };

    const permissionMap = {
      ddtUtilizationReportKey: {
        permission: "ddtUtilizationReport",
        condition: isFordBrand,
      },
      lincolnDdtUtilizationReportKey: {
        permission: "ddtUtilizationReport",
        condition: !isFordBrand,
      },
      openTransactionsReportKey: {
        permission: "dealerReporting:view",
        condition: isFordBrand,
      },
      purchaseRequestsReportKey: {
        permission: "purchaseRequestsReportNA",
        condition: isFordBrand,
      },
      dealerReservationsReportKey: {
        permission: "reservationsReportNA",
        condition: isFordBrand,
      },
      orderConversionReportKey: {
        permission: "ordersReportNA",
        condition: isFordBrand,
      },
      accessoriesReportKey: {
        permission: "accessoriesReportNA",
        customRender: renderAccessoriesReport,
        condition: isFordBrand,
      },
      modelEReportKey: { permission: "modelEReportNA", condition: isFordBrand },
    };

    const config = permissionMap[report.key];
    if (!config) return null;

    return (
      <HasPermissionTo
        permissions={reportPermissions}
        perform={[config.permission]}
        key={report.key}
        render={() =>
          config.customRender ? config.customRender() : getReport(report)
        }
        condition={config.condition}
      />
    );
  };

  const renderHeaderNoPermission = () => (
    <Typography variant="body1">{t("dealerReporting.subHeader")}</Typography>
  );

  const headerAndFilterPerformList = [
    "purchaseRequestsReportNA",
    "reservationsReportNA",
    "ordersReportNA",
    "modelEReportNA",
    "ddtUtilizationReport",
  ];

  return (
    <div>
      <SubNav
        title={t("dealerReporting.title")}
        {...props}
        history={history}
        t={t}
        user={user}
        userDetails={userDetails}
      />
      <div
        className={styles.pageContainer}
        style={{ borderTop: "1px solid #e5e5e5" }}
      >
        <HasPermissionTo
          permissions={reportPermissions}
          perform={headerAndFilterPerformList}
          render={() => (
            <Grid container>
              <Grid item xs={12} sm={10} md={8}>
                <Typography variant="h1" sx={{ my: 4 }}>
                  Reporting
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  {t("dealerReporting.subHeaderWithFilter")}
                </Typography>
              </Grid>
            </Grid>
          )}
          noRender={renderHeaderNoPermission}
        />

        <HasPermissionTo
          permissions={reportPermissions}
          perform={headerAndFilterPerformList}
          render={() => (
            <ReportFilter
              dateRangeMethod={dateRangeMethod}
              dateRange={dateRange}
              dateRanges={dateRanges}
              fromDate={fromDate}
              toDate={toDate}
              toggleRadio={toggleRadio}
              handleDateRangeChange={handleDateRangeChange}
              handleFromDateChange={handleFromDateChange}
              handleToDateChange={handleToDateChange}
              brand={userDetails.brand}
            />
          )}
        />
        <Grid container className={styles.gridHeader} sx={{ mt: 4 }}>
          <Grid item xs={6} sm={9}>
            <Typography variant="subtitle2">
              {t("dealerReporting.report")}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="subtitle2">
              {t("dealerReporting.action")}
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ mb: 20 }}>
          {reports
            .filter((report) => {
              if (isCanadaMarket) {
                return (
                  report.type === "ddtUtilization" ||
                  report.type === "ddtUtilizationLincoln"
                );
              }
              if (report.brandOnly === "ford") return isFordBrand;
              if (report.brandOnly === "lincoln") return !isFordBrand;
              return true;
            })
            .map((report) => getReportWithPermission(report))}
        </Box>
      </div>
    </div>
  );
};

NADealerReporting.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  t: PropTypes.func.isRequired,
  user: PropTypes.shape({
    brand: PropTypes.string.isRequired,
    lang: PropTypes.string,
    userType: PropTypes.string,
    permissions: PropTypes.shape({
      rolePermissions: PropTypes.oneOfType([PropTypes.array, PropTypes.object])
        .isRequired,
    }).isRequired,
  }).isRequired,
  userDetails: PropTypes.shape({
    brand: PropTypes.string,
  }).isRequired,
};

export default withTranslation("emp")(NADealerReporting);
